import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { createMarketplacePayment } from '../services/api';

const s = { fontSize: '14px', lineHeight: '1.8', color: '#8b949e' };
const h2 = { fontSize: '17px', fontWeight: 600, color: '#e6edf3', marginBottom: '10px', marginTop: '32px' };

export default function MarketplacePayment() {
    const { t } = useTranslation();
    usePageTitle(t('marketplace.paymentTitle'));
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null); // null | 'mp' | 'stripe'
    const [error, setError] = useState(null);
    const [checkout, setCheckout] = useState(null); // { checkoutUrl, provider } after API call

    const marketplaceToken = searchParams.get('marketplace_token') || '';
    const displayName = searchParams.get('display_name') || '';

    if (!marketplaceToken) {
        navigate('/marketplace');
        return null;
    }

    async function handlePay(provider) {
        setLoading(provider);
        setError(null);
        try {
            const data = await createMarketplacePayment(marketplaceToken, provider);
            setCheckout({ checkoutUrl: data.checkout_url, provider });
            setLoading(null);
        } catch {
            setLoading(null);
            setError(t('marketplace.paymentError'));
        }
    }

    if (checkout) {
        const isMp = checkout.provider === 'mercadopago';
        const btnColor = isMp ? '#009ee3' : '#635bff';
        const btnLabel = isMp ? t('marketplace.paymentGoToMp') : t('marketplace.paymentGoToStripe');
        return (
            <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
                <Header />
                <main style={{ flex: 1, maxWidth: '600px', margin: '0 auto', padding: '48px 24px', color: '#c9d1d9', textAlign: 'center' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>💳</div>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                        {t('marketplace.paymentTitle')}
                    </h1>
                    <p style={{ color: '#8b949e', fontSize: '14px', marginBottom: 32 }}>
                        {t('marketplace.paymentSubtitle')}
                    </p>

                    <a
                        href={checkout.checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block', padding: '14px 32px',
                            background: btnColor, color: '#fff', borderRadius: 8,
                            fontWeight: 700, fontSize: '16px', textDecoration: 'none',
                            marginBottom: 24,
                        }}
                    >
                        {btnLabel}
                    </a>

                    {isMp && (
                        <div style={{ borderTop: '1px solid #30363d', paddingTop: 28, marginTop: 8 }}>
                            <p style={{ color: '#8b949e', fontSize: '13px', marginBottom: 12 }}>
                                {t('marketplace.paymentAlreadyPaidHint')}
                            </p>
                            <button
                                onClick={() => navigate(`/marketplace/payment-success?marketplace_token=${marketplaceToken}`)}
                                style={{
                                    padding: '10px 24px', background: '#21262d',
                                    border: '1px solid #4caf50', borderRadius: 6,
                                    color: '#4caf50', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                                }}
                            >
                                ✓ {t('marketplace.paymentAlreadyPaid')}
                            </button>
                        </div>
                    )}
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <main style={{ flex: 1, maxWidth: '800px', margin: '0 auto', padding: '48px 24px', color: '#c9d1d9' }}>

                <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px', color: '#fff' }}>
                    {t('marketplace.paymentTitle')}
                </h1>
                <p style={{ fontSize: '13px', color: '#8b949e', marginBottom: '4px' }}>
                    {t('marketplace.paymentSubtitle')}
                </p>
                <p style={{ fontSize: '13px', color: '#4caf50', marginBottom: '32px' }}>
                    ✓ {t('marketplace.verifiedSeller')}: <strong style={{ color: '#e6edf3' }}>{displayName}</strong>
                </p>

                <p style={s}>{t('marketplace.paymentIntro')}</p>

                <h2 style={h2}>{t('marketplace.paymentWhyTitle')}</h2>
                <p style={s}>{t('marketplace.paymentWhyText')}</p>
                <p style={{ ...s, marginTop: '12px' }}>{t('marketplace.paymentWhyText2')}</p>

                <h2 style={h2}>{t('marketplace.paymentHowTitle')}</h2>
                <p style={s}>{t('marketplace.paymentHowText')}</p>

                {error && (
                    <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 14px', marginTop: 16, fontSize: '0.85rem' }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '16px', marginTop: '24px', flexWrap: 'wrap' }}>

                    {/* Mercado Pago */}
                    <button
                        onClick={() => handlePay('mercadopago')}
                        disabled={loading !== null}
                        style={{
                            flex: '1', minWidth: '220px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                            padding: '24px 20px',
                            background: '#21262d', border: '1px solid #30363d', borderRadius: '10px',
                            color: '#fff', cursor: loading !== null ? 'default' : 'pointer',
                            transition: 'border-color 0.2s', opacity: loading !== null ? 0.7 : 1,
                        }}
                        onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = '#009ee3'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#30363d'; }}
                    >
                        <span style={{ fontSize: '18px', fontWeight: 700, color: '#009ee3', letterSpacing: '-0.5px' }}>
                            mercado<span style={{ color: '#00b1ea' }}>pago</span>
                        </span>
                        <span style={{ fontSize: '22px', fontWeight: 700, color: '#e6edf3', marginTop: '4px' }}>
                            R$ 9,90
                        </span>
                        <span style={{ fontSize: '12px', color: '#8b949e' }}>
                            {t('marketplace.paymentMpDesc')} · PIX {t('marketplace.paymentOrCard')}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 600, marginTop: '4px', color: '#009ee3' }}>
                            {loading === 'mercadopago' ? t('common.loading') : t('marketplace.paymentMpBtn')}
                        </span>
                    </button>

                    {/* Stripe */}
                    <button
                        onClick={() => handlePay('stripe')}
                        disabled={loading !== null}
                        style={{
                            flex: '1', minWidth: '220px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                            padding: '24px 20px',
                            background: '#21262d', border: '1px solid #30363d', borderRadius: '10px',
                            color: '#fff', cursor: loading !== null ? 'default' : 'pointer',
                            transition: 'border-color 0.2s', opacity: loading !== null ? 0.7 : 1,
                        }}
                        onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = '#635bff'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#30363d'; }}
                    >
                        <img
                            src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
                            alt="Stripe"
                            style={{ height: '28px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                        />
                        <span style={{ fontSize: '22px', fontWeight: 700, color: '#e6edf3', marginTop: '4px' }}>
                            US$ 2
                        </span>
                        <span style={{ fontSize: '12px', color: '#8b949e' }}>
                            {t('marketplace.paymentStripeDesc')} · {t('marketplace.paymentCardOnly')}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 600, marginTop: '4px', color: '#635bff' }}>
                            {loading === 'stripe' ? t('common.loading') : t('marketplace.paymentStripeBtn')}
                        </span>
                    </button>

                </div>

            </main>
            <Footer />
        </div>
    );
}
